import React from 'react';

import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { KatSpinner } from '@amzn/katal-react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider, type QueryKey } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Init } from 'src/config/Auth';

import './i18n';

import App from './components/App';
import AppInitWrapper from './components/AppInitWrapper';
import store from './store/store';
import { handleAxiosError } from './utils/clientHelpers';
import { createSuccessToast } from './utils/helpers';

import '@amzn/meridian-tokens/base/color.css';

// Setup query cache
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 0 } },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.state.error && !query.meta?.skipErrorToast) {
        console.error('Failed to fetch page data:', error);
        handleAxiosError(query.state.error);
      }
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      const queriesToInvalidate = (mutation.meta?.invalidates || []) as QueryKey[];
      queriesToInvalidate.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
      createSuccessToast(mutation.meta?.successMessage as string);
    },
    onError: (error) => {
      console.error('Failed to persist page data:', error);
      handleAxiosError(error);
    },
  }),
});

// The Suspense component causes React to display the fallback KatSpinner until
// the i18n translation strings have been loaded.

Init();

ReactDOM.render(
  <React.StrictMode>
    <BrowserRouter>
      <Provider store={store}>
        <AppInitWrapper>
          <React.Suspense fallback={<KatSpinner size="large" />}>
            <QueryClientProvider client={queryClient}>
              <ReactQueryDevtools initialIsOpen={false} />
              <App />
            </QueryClientProvider>
          </React.Suspense>
        </AppInitWrapper>
      </Provider>
    </BrowserRouter>
  </React.StrictMode>,
  document.getElementById('root'),
);
